import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import ReportBuilder from './components/ReportBuilder';
import ReportTable from './components/ReportTable';
import ComplianceCharts from './components/ComplianceCharts';
import ReportTemplates from './components/ReportTemplates';
import ScheduledReports from './components/ScheduledReports';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const ComplianceReports = () => {
  const [activeTab, setActiveTab] = useState('builder');
  const [isGenerating, setIsGenerating] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const currentUser = {
    name: "Sarah Chen",
    role: "Compliance Officer"
  };

  const badgeCounts = {
    dashboard: 12,
    review: 8
  };

  const breadcrumbItems = [
    { label: 'Dashboard', href: '/content-analysis-dashboard' },
    { label: 'Compliance Reports' }
  ];

  const tabs = [
    { id: 'builder', label: 'Report Builder', icon: 'Settings', count: null },
    { id: 'templates', label: 'Templates', icon: 'FileText', count: 6 },
    { id: 'scheduled', label: 'Scheduled', icon: 'Calendar', count: 3 },
    { id: 'analytics', label: 'Analytics', icon: 'BarChart3', count: null }
  ];

  const handleGenerateReport = async (config) => {
    setIsGenerating(true);
    // Simulate API call
    setTimeout(() => {
      setReportData([]);
      setIsGenerating(false);
      setActiveTab('builder');
    }, 2000);
  };

  const handleExportReport = (format) => {
    console.log(`Exporting report in ${format} format`);
  };

  const handleSelectTemplate = (template) => {
    console.log('Selected template:', template);
    setActiveTab('builder');
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'builder':
        return (
          <div className="space-y-6">
            <ReportBuilder 
              onGenerateReport={handleGenerateReport}
              isGenerating={isGenerating}
            />
            {reportData?.length > 0 && (
              <ReportTable 
                reportData={reportData}
                onExport={handleExportReport}
              />
            )}
          </div>
        );
      case 'templates':
        return <ReportTemplates onSelectTemplate={handleSelectTemplate} />;
      case 'scheduled':
        return <ScheduledReports />;
      case 'analytics':
        return <ComplianceCharts />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header currentUser={currentUser} badgeCounts={badgeCounts} />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <Breadcrumb items={breadcrumbItems} className="mb-4" />
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">Compliance Reports</h1>
                <p className="text-muted-foreground">
                  Generate audit trails and analyze violation trends for regulatory oversight
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  iconName="Download"
                  iconPosition="left"
                >
                  Export All
                </Button>
                <Button
                  variant="default"
                  iconName="Plus"
                  iconPosition="left"
                >
                  New Report
                </Button>
              </div>
            </div>
          </div>

          {/* Mobile Tab Menu Toggle */}
          <div className="lg:hidden mb-6">
            <Button
              variant="outline"
              fullWidth
              iconName="Menu"
              iconPosition="left"
              onClick={() => setShowMobileMenu(!showMobileMenu)}
            >
              {tabs?.find(tab => tab?.id === activeTab)?.label}
            </Button>
          </div>

          {/* Tab Navigation */}
          <div className={`mb-8 ${showMobileMenu ? 'block' : 'hidden lg:block'}`}>
            <div className="border-b border-border">
              <nav className="flex flex-col lg:flex-row lg:space-x-8 space-y-2 lg:space-y-0">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => {
                      setActiveTab(tab?.id);
                      setShowMobileMenu(false);
                    }}
                    className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium border-b-2 transition-micro min-touch-target ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                    }`}
                  >
                    <Icon name={tab?.icon} size={16} />
                    <span>{tab?.label}</span>
                    {tab?.count && (
                      <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-white bg-accent rounded-full">
                        {tab?.count}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-8">
            {renderTabContent()}
          </div>

          {/* Quick Stats */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-card border border-border rounded-lg p-6 text-center">
              <div className="text-2xl font-bold text-foreground mb-1">47</div>
              <div className="text-sm text-muted-foreground">Reports Generated</div>
              <div className="text-xs text-success mt-1">+12% this month</div>
            </div>
            <div className="bg-card border border-border rounded-lg p-6 text-center">
              <div className="text-2xl font-bold text-foreground mb-1">6</div>
              <div className="text-sm text-muted-foreground">Active Templates</div>
              <div className="text-xs text-muted-foreground mt-1">2 recently updated</div>
            </div>
            <div className="bg-card border border-border rounded-lg p-6 text-center">
              <div className="text-2xl font-bold text-foreground mb-1">3</div>
              <div className="text-sm text-muted-foreground">Scheduled Reports</div>
              <div className="text-xs text-accent mt-1">Next run: Tomorrow</div>
            </div>
            <div className="bg-card border border-border rounded-lg p-6 text-center">
              <div className="text-2xl font-bold text-foreground mb-1">98%</div>
              <div className="text-sm text-muted-foreground">Delivery Success</div>
              <div className="text-xs text-success mt-1">All systems operational</div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ComplianceReports;